import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Notifications } from '@org/interfaces';
import { BehaviorSubject, Observable, of, Subject, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private apiUrl = `${environment.apiUrl}/api/notifications`;
  private notificationsSubject = new BehaviorSubject<Notifications[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  private totalNotificationsSubject = new BehaviorSubject<number>(0);
  public totalNotifications$ = this.totalNotificationsSubject.asObservable();
  private slideOutVisible2Source = new Subject<boolean>();
  slideOutVisible2$ = this.slideOutVisible2Source.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(AuthService) private authService: AuthService,
    private snackBar: MatSnackBar
  ) {}

  private showToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['custom-snackbar-container']
    });
  }

  getNotifications(): void {
    if (this.authService.isLoggedIn()) {
      this.http.get<Notifications[]>(`${this.apiUrl}`).subscribe(data => {
        this.notificationsSubject.next(data);
        this.totalNotificationsSubject.next(data.length);
      });
    }
  }

  markAsRead(notificationId: string): Observable<any> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<any>(`${this.apiUrl}/${notificationId}/read`, {}).pipe(
        tap(() => this.getNotifications()));
    }
    return of({});
  }

  markAsReadByTaskId(taskId: string): void {
    if (this.authService.isLoggedIn()) {
      this.http.patch<any>(`${this.apiUrl}/mark-read/${taskId}`, {}).subscribe({
        next: () => {
          this.getNotifications();
        }
      });
    }
  }

  markAllAsRead(): Observable<any> {
    if (this.authService.isLoggedIn()) {
      return this.http.post(`${this.apiUrl}/markAllAsRead`, {}).pipe(
        tap(() => this.getNotifications()));
    }
    return of({});
  }

  async setBadge(unreadCount: number): Promise<void> {
    if ('setAppBadge' in navigator) {
      try {
        await (navigator as any).setAppBadge(unreadCount);
      } catch (error) {
        console.error('Failed to set badge:', error);
      }
    }
  }

  async clearBadge(): Promise<void> {
    if ('clearAppBadge' in navigator) {
      try {
        await (navigator as any).clearAppBadge();
      } catch (error) {
        console.error('Failed to clear badge:', error);
      }
    }
  }

  async clearUnreadCount(unreadCount: number): Promise<void> {
    unreadCount = 0;
    await this.clearBadge();
  }

  async incrementUnreadCount(unreadCount: number): Promise<void> {
    unreadCount++;
    await this.setBadge(unreadCount);
  }

  openSlideOut(notification: any) {
    this.slideOutVisible2Source.next(notification);
  }

  addNotification(data: any) {
    this.getNotifications();
  }

  public showError(message: string): void {
    this.showToast(message);
  }
}
